import { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CartContext } from '@/App';
import { ShoppingCart, Minus, Plus, Package, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getProductById } from '@/data/products';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { cart, addToCart } = useContext(CartContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Load product from local data
    const productData = getProductById(id);
    if (productData) {
      setProduct(productData);
    } else {
      toast.error('Product not found');
      navigate('/products');
    }
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (product && quantity > 0) {
      addToCart(product, quantity);
      toast.success(`Added ${quantity} ${product.name} to cart`);
    }
  };

  if (!product) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="loading">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="product-detail-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="logo-container" data-testid="home-link">
            <img src={LOGO_URL} alt="Om Jishnu Logo" className="logo" />
            <span className="brand-name">OM JISHNU</span>
          </Link>
          <div className="nav-links">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/products" className="nav-link" data-testid="nav-products">Products</Link>
            <Link to="/admin" className="nav-link" data-testid="nav-admin">Admin</Link>
            <Link to="/checkout" className="cart-badge" data-testid="nav-cart">
              <ShoppingCart size={24} />
              {cart.length > 0 && (
                <span className="cart-count" data-testid="cart-count">{cart.length}</span>
              )}
            </Link>
          </div>
        </div>
      </nav>

      {/* Product Detail */}
      <div className="container" style={{ padding: '4rem 2rem' }} data-testid="product-detail">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '4rem', alignItems: 'start' }}>
          <div style={{ position: 'sticky', top: '100px' }}>
            <img
              src={product.image_url}
              alt={product.name}
              style={{ width: '100%', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
              data-testid="product-image"
            />
          </div>
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <span
                style={{
                  background: '#A44A66',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                data-testid="product-category"
              >
                {product.category}
              </span>
            </div>
            <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '0.5rem' }} data-testid="product-name">{product.name}</h1>
            <div style={{ color: '#666', fontSize: '1.2rem', marginBottom: '2rem', display: 'flex', alignItems: 'center' }} data-testid="product-brand">
              <Tag size={18} style={{ marginRight: '0.5rem' }} />
              {product.brand}
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#A44A66', marginBottom: '2rem' }} data-testid="product-price">
              ₹{product.price.toLocaleString()}
            </div>
            <div style={{ background: '#f8f8f8', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '0.5rem' }}>Description</h3>
              <p style={{ color: '#666', lineHeight: '1.8' }} data-testid="product-description">{product.description}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
              <Package size={20} style={{ color: '#A44A66' }} />
              <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>Stock:</span>
              <span
                style={{
                  color: product.stock > 0 ? '#10b981' : '#ef4444',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}
                data-testid="product-stock"
              >
                {product.stock > 0 ? `${product.stock} available` : 'Out of stock'}
              </span>
            </div>
            {product.stock > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>Quantity:</span>
                  <div style={{ display: 'flex', alignItems: 'center', border: '2px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      style={{ padding: '0.75rem 1rem', background: 'white', border: 'none', cursor: 'pointer' }}
                      data-testid="decrease-quantity"
                    >
                      <Minus size={20} />
                    </button>
                    <span style={{ padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '1.1rem' }} data-testid="quantity-display">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      style={{ padding: '0.75rem 1rem', background: 'white', border: 'none', cursor: 'pointer' }}
                      data-testid="increase-quantity"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
                <Button
                  className="btn-primary"
                  onClick={handleAddToCart}
                  style={{ width: '100%', padding: '1.25rem' }}
                  data-testid="add-to-cart-btn"
                >
                  Add to Cart
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: '#1a1a1a', color: 'white', padding: '3rem 2rem', textAlign: 'center', marginTop: '4rem' }} data-testid="footer">
        <div className="container">
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '60px', marginBottom: '1rem' }} />
          <p style={{ marginBottom: '0.5rem', fontSize: '1.2rem', fontWeight: '600' }}>OM JISHNU</p>
          <p style={{ color: '#999', marginBottom: '1.5rem' }}>Your trusted partner for printing solutions</p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>© 2025 Om Jishnu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ProductDetailPage;